import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  Skeleton,
  Alert,
  CircularProgress,
  MenuItem,
  Autocomplete,
  Chip,
  Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  NotificationsActive as NotificationsIcon,
  CalendarToday as CalendarIcon,
  Public as PublicIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import { WorkingHoursSettings } from '@/services/OrganizationSetupService';

interface WorkingHoursSectionProps {
  canEdit: boolean;
  isAdmin?: boolean;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const COMMON_TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST) - Asia/Kolkata' },
  { value: 'America/New_York', label: 'Eastern Time (ET) - America/New_York' },
  { value: 'America/Chicago', label: 'Central Time (CT) - America/Chicago' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - America/Denver' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - America/Los_Angeles' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT) - Europe/London' },
  { value: 'Europe/Paris', label: 'Central European Time (CET) - Europe/Paris' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET) - Europe/Berlin' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST) - Asia/Dubai' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT) - Asia/Singapore' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST) - Asia/Tokyo' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST) - Asia/Shanghai' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET) - Australia/Sydney' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time (NZT) - Pacific/Auckland' },
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' }
];

const InfoCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main + '15',
              color: theme.palette.primary.main,
              mr: 2
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
};

const InfoRow: React.FC<{
  label: string;
  value?: string | null;
  loading?: boolean;
}> = ({ label, value, loading }) => {
  if (loading) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Skeleton variant="text" width="60%" />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value || '-'}
      </Typography>
    </Box>
  );
};

const WorkingHoursSection: React.FC<WorkingHoursSectionProps> = ({ canEdit, isAdmin = false }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<WorkingHoursSettings>>({});

  const {
    workingHoursSettings,
    workingHoursLoading,
    loading,
    triggerLoading,
    notificationStatus,
    saveWorkingHoursSettings,
    fetchWorkingHoursSettings,
    fetchNotificationStatus,
    triggerNotifications
  } = useOrganizationSetupStore();

  useEffect(() => {
    fetchWorkingHoursSettings();
    fetchNotificationStatus();
  }, []);

  const handleTriggerNotifications = async () => {
    await triggerNotifications();
  };

  const formatLastTriggered = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';

    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    if (workingHoursSettings) {
      setFormData(workingHoursSettings);
    }
  }, [workingHoursSettings]);

  const handleStartTimeChange = (time: dayjs.Dayjs | null) => {
    setFormData(prev => ({
      ...prev,
      working_hours_start: time ? time.format('HH:mm') : '09:00'
    }));
  };

  const handleEndTimeChange = (time: dayjs.Dayjs | null) => {
    setFormData(prev => ({
      ...prev,
      working_hours_end: time ? time.format('HH:mm') : '18:00'
    }));
  };

  const handleDayToggle = (day: string) => {
    const currentDays = formData.working_days || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    setFormData(prev => ({
      ...prev,
      working_days: newDays
    }));
  };

  const handleNotificationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      attendance_notification_enabled: event.target.checked
    }));
  };

  const handleGraceMinutesChange = (field: 'late_punch_in_grace_minutes' | 'missing_punch_out_grace_minutes') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleTimezoneChange = (_event: React.SyntheticEvent, newValue: { value: string; label: string } | null) => {
    setFormData(prev => ({
      ...prev,
      timezone: newValue?.value || 'Asia/Kolkata'
    }));
  };

  const handleSave = async () => {
    const success = await saveWorkingHoursSettings(formData);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (workingHoursSettings) {
      setFormData(workingHoursSettings);
    }
    setIsEditing(false);
  };

  const formatWorkingDays = (days: string[]) => {
    if (!days || days.length === 0) return '-';
    return days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ');
  };

  // View Mode
  if (!isEditing) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Working Hours & Attendance
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              sx={{ borderRadius: 2 }}
            >
              Edit Settings
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Office Hours Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="Office Hours" icon={<AccessTimeIcon />}>
              <InfoRow
                label="Start Time"
                value={workingHoursSettings?.working_hours_start || '09:00'}
                loading={workingHoursLoading}
              />
              <InfoRow
                label="End Time"
                value={workingHoursSettings?.working_hours_end || '18:00'}
                loading={workingHoursLoading}
              />
            </InfoCard>
          </Grid>

          {/* Working Days Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="Working Days" icon={<CalendarIcon />}>
              <InfoRow
                label="Days"
                value={formatWorkingDays(workingHoursSettings?.working_days || [])}
                loading={workingHoursLoading}
              />
            </InfoCard>
          </Grid>

          {/* Timezone Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="Timezone" icon={<PublicIcon />}>
              <InfoRow
                label="Notification Timezone"
                value={COMMON_TIMEZONES.find(tz => tz.value === workingHoursSettings?.timezone)?.label || workingHoursSettings?.timezone || 'Asia/Kolkata'}
                loading={workingHoursLoading}
              />
            </InfoCard>
          </Grid>

          {/* Notification Settings Card */}
          <Grid size={{ xs: 12 }}>
            <InfoCard title="Attendance Notifications" icon={<NotificationsIcon />}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <InfoRow
                    label="Notifications Enabled"
                    value={workingHoursSettings?.attendance_notification_enabled ? 'Yes' : 'No'}
                    loading={workingHoursLoading}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <InfoRow
                    label="Late Punch-In Grace Period"
                    value={`${workingHoursSettings?.late_punch_in_grace_minutes || 30} minutes`}
                    loading={workingHoursLoading}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <InfoRow
                    label="Missing Punch-Out Grace Period"
                    value={`${workingHoursSettings?.missing_punch_out_grace_minutes || 30} minutes`}
                    loading={workingHoursLoading}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Manual Trigger Section - Admin Only */}
              {isAdmin && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Manual Trigger
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Tooltip title="Manually run all attendance notification checks now">
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={triggerLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                          onClick={handleTriggerNotifications}
                          disabled={triggerLoading || !workingHoursSettings?.attendance_notification_enabled}
                          sx={{ borderRadius: 2 }}
                        >
                          {triggerLoading ? 'Sending...' : 'Send Notifications Now'}
                        </Button>
                      </span>
                    </Tooltip>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        size="small"
                        label={`Last triggered: ${formatLastTriggered(notificationStatus?.lastTriggeredAt || null)}`}
                        color={notificationStatus?.lastTriggeredAt ? 'success' : 'default'}
                        variant="outlined"
                      />
                      {notificationStatus?.triggeredByName && (
                        <Typography variant="caption" color="text.secondary">
                          by {notificationStatus.triggeredByName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {!workingHoursSettings?.attendance_notification_enabled && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Enable notifications above to use manual trigger.
                    </Typography>
                  )}
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>How notifications work:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Late punch-in reminder is sent to employees who haven't punched in after the grace period.</li>
                    <li>Missing punch-out reminder is sent to employees who punched in but haven't punched out after the grace period.</li>
                    <li>End-of-day absence notification is sent to HR with managers in CC if an employee has no punch-in record and no approved leave.</li>
                    <li>Notifications are only sent on working days and skip holidays and approved leaves.</li>
                  </ul>
                </Typography>
              </Alert>
            </InfoCard>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Edit Mode
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Edit Working Hours & Attendance
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleCancel}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Office Hours */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Office Hours
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TimePicker
                      label="Start Time"
                      value={formData.working_hours_start ? dayjs(formData.working_hours_start, 'HH:mm') : dayjs('09:00', 'HH:mm')}
                      onChange={handleStartTimeChange}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: 'Office working hours start time'
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TimePicker
                      label="End Time"
                      value={formData.working_hours_end ? dayjs(formData.working_hours_end, 'HH:mm') : dayjs('18:00', 'HH:mm')}
                      onChange={handleEndTimeChange}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: 'Office working hours end time'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Working Days */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Working Days
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <FormGroup row>
                  {DAYS_OF_WEEK.map(day => (
                    <FormControlLabel
                      key={day.key}
                      control={
                        <Checkbox
                          checked={(formData.working_days || []).includes(day.key)}
                          onChange={() => handleDayToggle(day.key)}
                        />
                      }
                      label={day.label}
                      sx={{ minWidth: 140 }}
                    />
                  ))}
                </FormGroup>
              </Grid>

              {/* Timezone */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Timezone
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Autocomplete
                  options={COMMON_TIMEZONES}
                  getOptionLabel={(option) => option.label}
                  value={COMMON_TIMEZONES.find(tz => tz.value === formData.timezone) || COMMON_TIMEZONES[0]}
                  onChange={handleTimezoneChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Notification Timezone"
                      helperText="Timezone used for scheduling attendance notifications"
                    />
                  )}
                  sx={{ maxWidth: 500 }}
                />
              </Grid>

              {/* Notification Settings */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Notification Settings
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.attendance_notification_enabled ?? true}
                      onChange={handleNotificationToggle}
                      color="primary"
                    />
                  }
                  label="Enable Attendance Email Notifications"
                  sx={{ mb: 3 }}
                />

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Late Punch-In Grace Period"
                      value={formData.late_punch_in_grace_minutes ?? 30}
                      onChange={handleGraceMinutesChange('late_punch_in_grace_minutes')}
                      InputProps={{
                        endAdornment: <Typography color="text.secondary">minutes</Typography>
                      }}
                      helperText="Time after office start before sending late notification"
                      disabled={!formData.attendance_notification_enabled}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Missing Punch-Out Grace Period"
                      value={formData.missing_punch_out_grace_minutes ?? 30}
                      onChange={handleGraceMinutesChange('missing_punch_out_grace_minutes')}
                      InputProps={{
                        endAdornment: <Typography color="text.secondary">minutes</Typography>
                      }}
                      helperText="Time after office end before sending punch-out reminder"
                      disabled={!formData.attendance_notification_enabled}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default WorkingHoursSection;
